
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { buildProfileHref } from '@/utils/username';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ModalOverlay from '../ModalOverlay/ModalOverlay';
import SaveDestinationPickerPanel, {
  type SaveDestinationSelection,
} from './SaveDestinationPickerPanel';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_SAVE_CLICKED,
  EVENT_PLACE_SAVED_TO_NEW_ITINERARY,
  EVENT_BOOKMARK_CLICKED,
  EVENT_RECS_BANNER_LINK_CLICKED,
} from '@/lib/analyticsEvents';
import { PROFILE_OPEN_RECS_QUERY, PROFILE_OPEN_RECS_VALUE } from '@/hooks/usePlacePublishedShareCta';
import type { PlaceCardData } from '@/types/listViewTypes';
import type { PostPresentationActivity } from '@/components/blocks/PostPresentation/types';
import { createItineraryWithPlace, createListWithPlace, type SavedItinerary } from '@/api/itinerary';
import { savePost } from '@/api/post';
import { useAuth } from '@/contexts';
import { debug, error as logError } from '@/utils/logger';
import { primaryDestinationFromPlaceCard } from '@/utils/placePrimaryDestination';
import type { PrimaryDestination } from '@/types/city';

export type { SavedItinerary, SaveDestinationSelection };

interface SaveToOverlayProps {
  isOpen: boolean;
  onClose?: () => void;
  onSaveToItinerary?: (itineraryId: string) => void | Promise<void>;
  savedItineraries?: SavedItinerary[];
  place?: PlaceCardData | null;
  originalActivity?: PostPresentationActivity | null;
  isLoading?: boolean;
  savedLists?: SavedItinerary[];
  isLoadingLists?: boolean;
  onSaveToList?: (listId: string) => void | Promise<void>;
  onNewTripOrListCreated?: () => void;
  title?: string;
  subtitle?: string;
  preOpenBanner?: string;
  linkPreOpenBannerRecommendations?: boolean;
  overlayClassName?: string;
  destinationSelectionOnly?: boolean;
  onDestinationSelected?: (selection: SaveDestinationSelection) => Promise<void> | void;
  newDestinationPrimaryDestination?: PrimaryDestination | null;
  newDestinationCityIdentifyingName?: string | null;
}

interface SaveSuccess {
  itineraryId: string;
  itineraryName: string;
  href: string;
  bookmarked: boolean;
  savesProfileHref?: string;
}

function BookmarkFilledIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4.33389 0H4.83389C4.83533 0 4.83678 9.42139e-06 4.83823 2.82302e-05C4.84162 9.42072e-06 4.84502 0 4.84842 0H11.1527L11.1629 2.8235e-05L11.1672 0H11.6672C12.0208 0 12.36 0.140476 12.61 0.390524C12.8601 0.640573 13.0005 0.979711 13.0005 1.33333V14.9893C13.0197 15.185 12.9769 15.3816 12.8782 15.5516C12.7795 15.7216 12.6299 15.8563 12.4505 15.9367C12.3486 15.9784 12.2394 15.9999 12.1292 16C12.09 15.9988 12.051 15.9953 12.0124 15.9897C11.8949 16.0024 11.7724 16.0034 11.6531 15.9924C11.4886 15.9772 11.3375 15.9399 11.2191 15.8851L8.59067 14.6792C8.51191 14.6428 8.41834 14.6139 8.31534 14.5941C8.21233 14.5744 8.1019 14.5643 7.99038 14.5643C7.87885 14.5643 7.76842 14.5744 7.66541 14.5941C7.56241 14.6139 7.46884 14.6428 7.39008 14.6792L4.77526 15.8851C4.65603 15.939 4.50494 15.9755 4.34095 15.9899C4.23246 15.9995 4.12152 15.9992 4.01429 15.9892C3.85836 16.0109 3.69881 15.9931 3.55055 15.9367C3.37118 15.8563 3.22159 15.7216 3.1229 15.5516C3.02421 15.3816 2.98141 15.185 3.00055 14.9893V1.33333C3.00055 0.979711 3.14103 0.640573 3.39108 0.390524C3.64112 0.140476 3.98026 0 4.33389 0Z"
        fill="#252528"
      />
    </svg>
  );
}

const SaveToOverlay: React.FC<SaveToOverlayProps> = ({
  isOpen,
  onClose,
  onSaveToItinerary,
  savedItineraries = [],
  place,
  originalActivity: _originalActivity,
  isLoading = false,
  savedLists = [],
  isLoadingLists = false,
  onSaveToList,
  onNewTripOrListCreated,
  title,
  subtitle,
  preOpenBanner,
  linkPreOpenBannerRecommendations = false,
  overlayClassName,
  destinationSelectionOnly = false,
  onDestinationSelected,
  newDestinationPrimaryDestination = null,
  newDestinationCityIdentifyingName = null,
}) => {
  const { user } = useAuth();
  const router = useNavigate();
  const [saveSuccess, setSaveSuccess] = useState<SaveSuccess | null>(null);
  const [isNavigatingToSaves, setIsNavigatingToSaves] = useState(false);
  const [isSavingToItinerary, setIsSavingToItinerary] = useState(false);
  const [isSavingToList, setIsSavingToList] = useState(false);
  const [isCreatingItinerary, setIsCreatingItinerary] = useState(false);
  const [isCreatingList, setIsCreatingList] = useState(false);

  const resolvedPrimaryDestination = useMemo(
    () => newDestinationPrimaryDestination ?? primaryDestinationFromPlaceCard(place),
    [newDestinationPrimaryDestination, place],
  );
  const resolvedCityIdentifyingNames = useMemo(() => {
    const values = [
      newDestinationCityIdentifyingName?.trim() || '',
      resolvedPrimaryDestination?.identifying_name?.trim() || '',
    ].filter(Boolean);
    return Array.from(new Set(values));
  }, [newDestinationCityIdentifyingName, resolvedPrimaryDestination?.identifying_name]);

  const bookmarkPostForUser = async (postId: string): Promise<boolean> => {
    try {
      await savePost(postId);
      WebAnalytics.trackContentInteraction(EVENT_BOOKMARK_CLICKED, {
        postID: postId,
        action: 'bookmark',
      });
      return true;
    } catch (err) {
      logError('[SaveToOverlay] Auto-bookmark failed:', err);
      toast.error("Saved to the trip, but couldn't add to your Saves. Try the bookmark on the card.");
      return false;
    }
  };

  const buildSaveSuccess = useCallback((postId: string, name: string, href: string, bookmarked: boolean): SaveSuccess => ({
    itineraryId: postId,
    itineraryName: name,
    href,
    bookmarked,
    savesProfileHref: user?._id
      ? `${buildProfileHref({ _id: user._id, username: user.username })}?tab=saves`
      : undefined,
  }), [user?._id, user?.username]);

  useEffect(() => {
    if (!isOpen) {
      setSaveSuccess(null);
      setIsNavigatingToSaves(false);
      setIsSavingToItinerary(false);
      setIsSavingToList(false);
      setIsCreatingItinerary(false);
      setIsCreatingList(false);
    }
  }, [isOpen]);

  const handleSavesLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string | undefined) => {
      e.preventDefault();
      if (!href || isNavigatingToSaves) return;
      setIsNavigatingToSaves(true);
      window.setTimeout(() => {
        try {
          router(href);
        } catch {
          setIsNavigatingToSaves(false);
          toast.error("Couldn't open your Saves. Try again.");
        }
      }, 0);
    },
    [isNavigatingToSaves, router],
  );

  const isActiveOperation =
    isSavingToItinerary ||
    isSavingToList ||
    isCreatingItinerary ||
    isCreatingList ||
    isNavigatingToSaves;
  const blockBackdropDismiss = !!saveSuccess || isActiveOperation;
  const closeButtonDisabled = isActiveOperation;
  const controlsLocked = !!saveSuccess || isActiveOperation;

  const handlePickItinerary = useCallback(
    async (itinerary: SavedItinerary) => {
      if (!onSaveToItinerary || controlsLocked) return;

      WebAnalytics.trackContentInteraction(EVENT_SAVE_CLICKED, {
        itineraryId: itinerary.id,
        itineraryName: itinerary.name,
      });

      setIsSavingToItinerary(true);
      try {
        await onSaveToItinerary(itinerary.id);
        if (destinationSelectionOnly) {
          await onDestinationSelected?.({
            destinationType: 'itinerary',
            destinationId: itinerary.id,
            destinationName: itinerary.name,
            isNew: false,
          });
          onClose?.();
          return;
        }
        const bookmarked = await bookmarkPostForUser(itinerary.id);
        setSaveSuccess(
          buildSaveSuccess(itinerary.id, itinerary.name, `/itinerary/${itinerary.id}`, bookmarked),
        );
        onNewTripOrListCreated?.();
      } catch {
        toast.error('Failed to add place to itinerary. Please try again.');
      } finally {
        setIsSavingToItinerary(false);
      }
    },
    [
      controlsLocked,
      destinationSelectionOnly,
      onClose,
      onDestinationSelected,
      onNewTripOrListCreated,
      onSaveToItinerary,
      buildSaveSuccess,
    ],
  );

  const handlePickList = useCallback(
    async (list: SavedItinerary) => {
      if (!onSaveToList || controlsLocked) return;

      setIsSavingToList(true);
      try {
        await onSaveToList(list.id);
        if (destinationSelectionOnly) {
          await onDestinationSelected?.({
            destinationType: 'list',
            destinationId: list.id,
            destinationName: list.name,
            isNew: false,
          });
          onClose?.();
          return;
        }
        const bookmarked = await bookmarkPostForUser(list.id);
        setSaveSuccess(buildSaveSuccess(list.id, list.name, `/list/${list.id}`, bookmarked));
        onNewTripOrListCreated?.();
      } catch {
        toast.error('Failed to add place to list. Please try again.');
      } finally {
        setIsSavingToList(false);
      }
    },
    [
      controlsLocked,
      destinationSelectionOnly,
      onClose,
      onDestinationSelected,
      onNewTripOrListCreated,
      onSaveToList,
      buildSaveSuccess,
    ],
  );

  const handleCreateNewItinerary = useCallback(
    async (itineraryTitle: string) => {
      if (!place) return;

      setIsCreatingItinerary(true);
      try {
        debug('[SaveToOverlay] Creating draft itinerary with place', {
          placeTitle: place.title,
          placeId: place.id,
        });

        const createdPost = await createItineraryWithPlace(
          itineraryTitle,
          place,
          true,
          destinationSelectionOnly
            ? {
                primaryDestination: resolvedPrimaryDestination,
                cityIdentifyingNames: resolvedCityIdentifyingNames,
              }
            : {},
        );

        WebAnalytics.trackContentInteraction(EVENT_PLACE_SAVED_TO_NEW_ITINERARY, {
          placeId: place.id,
          placeTitle: place.title,
          itineraryId: createdPost._id,
          itineraryName: itineraryTitle,
        });

        if (destinationSelectionOnly) {
          await onDestinationSelected?.({
            destinationType: 'itinerary',
            destinationId: createdPost._id,
            destinationName: itineraryTitle,
            isNew: true,
            seedPlaceId: place.id,
          });
          onNewTripOrListCreated?.();
          onClose?.();
          return;
        }

        const bookmarked = await bookmarkPostForUser(createdPost._id);
        setSaveSuccess(
          buildSaveSuccess(
            createdPost._id,
            itineraryTitle,
            `/itinerary/${createdPost._id}`,
            bookmarked,
          ),
        );
        onNewTripOrListCreated?.();
      } catch (err) {
        logError('[SaveToOverlay] Error creating draft itinerary:', err);
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { status?: number } };
          const status = axiosError.response?.status;
          if (status === 403) {
            toast.error("You don't have permission to create an itinerary");
          } else if (status === 400) {
            toast.error('Invalid data. Please try again.');
          } else if (status === 500 || status === 502 || status === 503) {
            toast.error('Server error. Please try again later.');
          } else {
            toast.error('Failed to create itinerary. Please try again.');
          }
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          if (msg.includes('Network Error') || msg.includes('Failed to fetch')) {
            toast.error('Network error. Please check your connection and try again.');
          } else {
            toast.error('Failed to create itinerary. Please try again.');
          }
        }
      } finally {
        setIsCreatingItinerary(false);
      }
    },
    [
      destinationSelectionOnly,
      onClose,
      onDestinationSelected,
      onNewTripOrListCreated,
      place,
      resolvedCityIdentifyingNames,
      resolvedPrimaryDestination,
      buildSaveSuccess,
    ],
  );

  const handleCreateNewList = useCallback(
    async (listTitle: string) => {
      if (!place) return;

      setIsCreatingList(true);
      try {
        debug('[SaveToOverlay] Creating list with place', {
          placeTitle: place.title,
          placeId: place.id,
        });

        const createdPost = await createListWithPlace(
          place,
          listTitle,
          destinationSelectionOnly
            ? {
                primaryDestination: resolvedPrimaryDestination,
                cityIdentifyingNames: resolvedCityIdentifyingNames,
              }
            : {},
        );

        if (destinationSelectionOnly) {
          await onDestinationSelected?.({
            destinationType: 'list',
            destinationId: createdPost._id,
            destinationName: listTitle,
            isNew: true,
            seedPlaceId: place.id,
          });
          onNewTripOrListCreated?.();
          onClose?.();
          return;
        }

        const bookmarked = await bookmarkPostForUser(createdPost._id);
        setSaveSuccess(
          buildSaveSuccess(createdPost._id, listTitle, `/list/${createdPost._id}`, bookmarked),
        );
        onNewTripOrListCreated?.();
      } catch (err) {
        logError('[SaveToOverlay] Error creating list with place:', err);
        toast.error('Failed to create list. Please try again.');
      } finally {
        setIsCreatingList(false);
      }
    },
    [
      destinationSelectionOnly,
      onClose,
      onDestinationSelected,
      onNewTripOrListCreated,
      place,
      resolvedCityIdentifyingNames,
      resolvedPrimaryDestination,
      buildSaveSuccess,
    ],
  );

  const tabPanelPrefix = (
    <>
      {preOpenBanner && !saveSuccess ? (
        <div
          role="status"
          aria-live="polite"
          className="mx-6 mt-4 flex items-center gap-2 rounded-2xl bg-[#E7E7FF] px-4 py-3"
        >
          <FavoriteBorderIcon sx={{ fontSize: 18, color: '#252528', flexShrink: 0 }} />
          {linkPreOpenBannerRecommendations && user?._id ? (
            <span className="text-sm font-normal text-[#525257]">
              Added to your{' '}
              <Link
                to={`${buildProfileHref({ _id: user._id, username: user.username })}?${PROFILE_OPEN_RECS_QUERY}=${PROFILE_OPEN_RECS_VALUE}`}
                onClick={() => {
                  debug('[SaveToOverlay] Recs banner link clicked');
                  WebAnalytics.track(EVENT_RECS_BANNER_LINK_CLICKED, {
                    user_id: user._id,
                  });
                  onClose?.();
                }}
                className="font-semibold text-[#371168] underline underline-offset-2 hover:text-[#250d4a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#371168]"
              >
                recommendations
              </Link>
              !
            </span>
          ) : (
            <span className="text-sm font-normal text-[#525257]">{preOpenBanner}</span>
          )}
        </div>
      ) : null}

      {saveSuccess ? (
        <div
          role="status"
          aria-live="polite"
          className="mx-6 mt-4 rounded-2xl border border-[#e1e1e7] bg-[#E7E7FF] px-4 py-4 text-sm leading-relaxed"
        >
          <div className="flex min-w-0 items-start gap-2">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
              <AddCircleOutlineIcon sx={{ fontSize: 16, color: '#252528' }} />
            </span>
            <p className="m-0 min-w-0 flex-1 text-sm font-normal leading-relaxed text-[#525257]">
              <span className="break-words">
                {(place?.title || 'This place').trim() || 'This place'}
              </span>{' '}
              <span>added to</span>{' '}
              <Link
                to={saveSuccess.href}
                onClick={() => onClose?.()}
                className="inline break-words font-semibold text-[#371168] underline underline-offset-2 hover:text-[#250d4a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#371168]"
              >
                {saveSuccess.itineraryName}
              </Link>
              .
            </p>
          </div>
          {saveSuccess.bookmarked && saveSuccess.savesProfileHref ? (
            <div className="mt-3 flex min-w-0 items-center gap-2">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
                <BookmarkFilledIcon />
              </span>
              <div className="min-w-0 flex-1 rounded-full bg-[#E8E8FF] py-2 pl-0 pr-3 text-sm font-normal leading-relaxed text-[#525257]">
                <p className="m-0 min-w-0">
                  <Link
                    to={saveSuccess.href}
                    onClick={() => onClose?.()}
                    className="inline break-words font-semibold text-[#371168] underline underline-offset-2 hover:text-[#250d4a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#371168]"
                  >
                    {saveSuccess.itineraryName}
                  </Link>{' '}
                  <span>saved to profile </span>
                  <Link
                    to={saveSuccess.savesProfileHref}
                    onClick={(e) => handleSavesLinkClick(e, saveSuccess.savesProfileHref)}
                    className={`inline font-semibold text-[#371168] underline underline-offset-2 hover:text-[#250d4a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#371168] ${isNavigatingToSaves ? 'pointer-events-none opacity-70' : ''}`}
                  >
                    Saves
                  </Link>
                  .
                </p>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {(isSavingToItinerary || isSavingToList) && !saveSuccess ? (
        <div className="flex items-center gap-2 px-6 py-3 text-sm text-[#525257]">
          <LoadingSpinner size="small" />
          <span>Saving…</span>
        </div>
      ) : null}
    </>
  );

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClose={blockBackdropDismiss ? undefined : onClose} className={overlayClassName}>
      <div className="mx-auto w-full max-w-[500px] px-4" onClick={(e) => e.stopPropagation()}>
        <SaveDestinationPickerPanel
          variant="overlay"
          active={isOpen}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          savedItineraries={savedItineraries}
          savedLists={savedLists}
          isLoading={isLoading}
          isLoadingLists={isLoadingLists}
          place={place}
          controlsLocked={controlsLocked}
          closeButtonDisabled={closeButtonDisabled}
          tabPanelPrefix={tabPanelPrefix}
          blockingOverlay={
            isNavigatingToSaves ? (
              <div
                className="pointer-events-auto absolute inset-0 z-[60] flex cursor-wait items-center justify-center rounded-2xl bg-white/75"
                aria-busy="true"
                aria-label="Opening Saves"
              >
                <LoadingSpinner size="medium" color="primary" />
              </div>
            ) : null
          }
          onPickItinerary={handlePickItinerary}
          onPickList={handlePickList}
          onCreateNewItinerary={handleCreateNewItinerary}
          onCreateNewList={handleCreateNewList}
        />
      </div>
    </ModalOverlay>
  );

  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default SaveToOverlay;
